import { useFeatureAccess } from './platformAccess';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useContext, useState, useEffect } from 'react';
import { UserContext } from '../components/auth/auth-context';
import { clockForCalendar } from './clockSystem';

const GET = gql`query CalendarPreferences($businessId: Int) { calendarPreferences(businessId: $businessId) { displayCalendar defaultCalendar displayClock timezone } }`;
const SAVE = gql`mutation SaveCalendarPreferences($businessId: Int, $displayCalendar: String, $displayClock: String, $defaultCalendar: String) { saveCalendarPreferences(businessId: $businessId, displayCalendar: $displayCalendar, displayClock: $displayClock, defaultCalendar: $defaultCalendar) { displayCalendar defaultCalendar displayClock timezone } }`;
export function useCalendarPreferences(businessId, initialCalendar = 'gregory') {
  const { providerUser } = useContext(UserContext);
  const { ethiopianEnabled } = useFeatureAccess();
  const userId = providerUser?.userId;
  const key = `timely-calendar:${userId || 'guest'}`;
  const read = () => { try { const saved = localStorage.getItem(key); return saved === 'ethiopic' || saved === 'gregory' ? saved : initialCalendar; } catch { return initialCalendar; } };
  const [displayCalendar, setDisplay] = useState(read);
  useEffect(() => {
    const sync = event => {
      if (event.detail?.key === key || event.key === key) setDisplay(read());
      if (event.detail?.key === `${key}:clock` || event.key === `${key}:clock`) setClockPreference(readClock());
    };
    window.addEventListener('timely-calendar-preference', sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener('timely-calendar-preference', sync); window.removeEventListener('storage', sync); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const [preferenceError, setError] = useState('');
  const clockKey = `${key}:clock`;
  const readClock = () => { try { const value = localStorage.getItem(clockKey); return ['standard', 'ethiopian', 'both'].includes(value) ? value : 'auto'; } catch { return 'auto'; } };
  const [clockPreference, setClockPreference] = useState(readClock);
  useEffect(() => { setClockPreference(readClock()); }, [clockKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const displayClock = clockPreference === 'auto' ? clockForCalendar(displayCalendar) : clockPreference;
  async function setDisplayClock(value) {
    if (['ethiopian', 'both'].includes(value) && !ethiopianEnabled) return;
    if (!['auto', 'standard', 'ethiopian', 'both'].includes(value)) return;
    setClockPreference(value);
    try { localStorage.setItem(clockKey, value); } catch { /* Device preference only. */ }
    window.dispatchEvent(new CustomEvent('timely-calendar-preference', { detail: { key: clockKey } }));
    try { if (userId && value !== 'auto') await save({ variables: { displayClock: value } }); } catch { setError('Time format changed on this device, but could not be saved to your account.'); }
  }
  const { data, refetch } = useQuery(GET, { variables: { businessId: Number(businessId) || null }, skip: !userId, fetchPolicy: 'cache-and-network' });
  const [save, { loading: savingPreference }] = useMutation(SAVE, { refetchQueries: ['CalendarPreferences'], awaitRefetchQueries: true });
  useEffect(() => { setDisplay(read()); }, [key, initialCalendar]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data?.calendarPreferences) {
    setDisplay(data.calendarPreferences.displayCalendar);
    if (data.calendarPreferences.displayClock) {
      setClockPreference(data.calendarPreferences.displayClock);
      try { localStorage.setItem(clockKey, data.calendarPreferences.displayClock); } catch {}
    }
  } }, [data, clockKey]);
  async function setDisplayCalendar(value) {
    if (value === 'ethiopic' && !ethiopianEnabled) return;
    const nextClock = clockPreference === 'both' ? 'both' : clockForCalendar(value);
    setDisplay(value); setClockPreference(nextClock); setError('');
    try {
      localStorage.setItem(key, value);
      localStorage.setItem(clockKey, nextClock);
    } catch { /* Private browsing can disable storage. */ }
    window.dispatchEvent(new CustomEvent('timely-calendar-preference', { detail: { key } }));
    window.dispatchEvent(new CustomEvent('timely-calendar-preference', { detail: { key: clockKey } }));
    try { if (userId) await save({ variables: { displayCalendar: value, displayClock: nextClock } }); }
    catch { setError('Calendar changed on this device, but could not be saved to your account.'); }
  }
  async function setDefaultCalendar(value) {
    setError('');
    try { await save({ variables: { businessId: Number(businessId), defaultCalendar: value } }); await refetch(); }
    catch (error) { setError(error.message); }
  }
  return { ethiopianEnabled, displayCalendar: ethiopianEnabled ? displayCalendar : 'gregory', setDisplayCalendar, displayClock: ethiopianEnabled ? displayClock : 'standard', clockPreference, setDisplayClock, timezone: data?.calendarPreferences?.timezone || providerUser?.timeZone?.timezone, defaultCalendar: ethiopianEnabled ? (data?.calendarPreferences?.defaultCalendar || 'gregory') : 'gregory', setDefaultCalendar, preferenceError, savingPreference };
}
